import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useVersionHistoryPreview } from "@/components/planHistory/VersionHistoryHost";
import { Redo2, Undo2 } from "lucide-react";
import { useEffect } from "react";
import { toast } from "sonner";

const EDITOR_UNDO_TOAST_ID = "editor-undo";

const isMacShortcut =
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad/.test(navigator.platform);

const undoShortcut = isMacShortcut ? "⌘Z" : "Ctrl+Z";
const redoShortcut = isMacShortcut ? "⌘⇧Z" : "Ctrl+Y";

export const showEditorUndoToast = (kind: "undo" | "redo") => {
  const Icon = kind === "undo" ? Undo2 : Redo2;
  toast(kind === "undo" ? "Undone" : "Redone", {
    id: EDITOR_UNDO_TOAST_ID,
    duration: 1500,
    icon: <Icon className="h-4 w-4" />,
  });
};

const isInOverlay = (target: EventTarget | null) => {
  if (!(target instanceof Element)) return false;
  return Boolean(
    target.closest("[role='dialog']") ||
      target.closest("[role='menu']") ||
      target.closest("[role='listbox']") ||
      target.closest("[data-radix-popper-content-wrapper]"),
  );
};

const isUndoRedoEvent = (event: KeyboardEvent) => {
  if (!(event.metaKey || event.ctrlKey) || event.altKey) return null;
  const key = event.key.toLowerCase();
  if (key === "z") return event.shiftKey ? "redo" : "undo";
  if (key === "y" && !event.shiftKey) return "redo";
  return null;
};

export function EditorUndoControls({
  canUndo,
  canRedo,
  disabled,
  onUndo,
  onRedo,
}: {
  canUndo: boolean;
  canRedo: boolean;
  disabled?: boolean;
  onUndo: () => boolean | void;
  onRedo: () => boolean | void;
}) {
  const isPreviewing = useVersionHistoryPreview();
  const isDisabled = Boolean(disabled || isPreviewing);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const kind = isUndoRedoEvent(event);
      if (!kind || isDisabled || isInOverlay(event.target)) return;
      event.preventDefault();
      event.stopPropagation();
      const didApply = kind === "undo" ? onUndo() : onRedo();
      if (didApply) showEditorUndoToast(kind);
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [isDisabled, onRedo, onUndo]);

  const handleUndo = () => {
    if (isDisabled) return;
    if (onUndo()) showEditorUndoToast("undo");
  };

  const handleRedo = () => {
    if (isDisabled) return;
    if (onRedo()) showEditorUndoToast("redo");
  };

  return (
    <TooltipProvider delayDuration={200}>
      <ButtonGroup className="shrink-0 flex-nowrap">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="shrink-0"
              disabled={isDisabled || !canUndo}
              onClick={handleUndo}
              aria-label={`Undo (${undoShortcut})`}
            >
              <Undo2 className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Undo {undoShortcut}</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="shrink-0"
              disabled={isDisabled || !canRedo}
              onClick={handleRedo}
              aria-label={`Redo (${redoShortcut})`}
            >
              <Redo2 className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Redo {redoShortcut}</TooltipContent>
        </Tooltip>
      </ButtonGroup>
    </TooltipProvider>
  );
}
